/**
 * Pinta un widget (tabla, KPI, chart, texto, contenedor) y pide sus datos con engineQuery,
 * que agrupa en un solo /api/engine/query/batch las consultas de la página.
 * Con el tema «Ejecutivo» (dashboardTheme.js) aplica el acabado limpio: tooltip propio,
 * grid tenue, números abreviados, fechas legibles, dona con total y tabla sobria.
 */
import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import {
    BarChart, Bar, LineChart, Line, AreaChart, Area, PieChart, Pie, Cell, ComposedChart,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList
} from 'recharts';
import { geoMercator, geoPath } from 'd3-geo';
import { KPI_TARGET_KEY, engineQuery, peekQueryResult, isQueryResultTrusted, currentEngineDataVersion, subscribeEngineData, buildWidgetQueryPayload, widgetHasQueryShape, sourceRequestFields, CHART_PALETTE, DEFAULT_CANVAS, INNER_CANVAS_COLUMNS, normalizeWidgetType, getWidgetStyle, getTextContentStyle, getBlockTitleStyle, getBlockSubtitleStyle, getBlockFrameStyle, formatNumber, parseMetricFormat, isDecorationType, isChartType, isContainerType, hugsBlockTop, widgetCardChrome } from './engineConfig';
import { getAlias, buildEngineFilters } from './filterUtils';
import { evalNumericFormula, firstConditionalStyle, visibleMetricList } from './fieldActions';
import { MEXICO_STATES_GEOJSON, matchStateFeature } from './mexicoStates';
import FilterDropdown from './FilterDropdown';
import MeasuredGrid from './MeasuredGrid';
import { stackWidgets, stackedHeight } from './stackLayout';
import WidgetExportMenu from './WidgetExportMenu';
import { exportElementToPng, exportElementToPdf, exportWidgetToExcel, sanitizeFilename } from './exportUtils';
import {
    useDashboardTheme, EXEC, EXEC_PALETTE, EXEC_MAX_BAR, OTHERS_LABEL, CLASSIC_TABLE_DEFAULTS,
    compactNumber, isDateCategory, dateCategoryFormatter, defaultChartOrder, foldPieSlices, themed,
    isOthersRow, splitOthers, grainLabelFormatter
} from './dashboardTheme';

// Extremos por defecto de la rampa secuencial azul (100/700) de la skill dataviz —
// misma familia que CHART_PALETTE[1]. El usuario puede sustituirlos desde Propiedades.
const MAP_COLOR_MIN_DEFAULT = '#cde2fb';
const MAP_COLOR_MAX_DEFAULT = '#0d366b';
const MAP_NO_DATA_COLOR = '#e1e0d9';
const MAP_ZOOM_MIN = 1;
const MAP_ZOOM_MAX = 8;
const MAP_ZOOM_STEP = 1.4;

function hexToRgb(hex) {
    const clean = String(hex || '').replace('#', '');
    const full = clean.length === 3 ? clean.split('').map(c => c + c).join('') : clean;
    const num = parseInt(full, 16);
    if (Number.isNaN(num)) return { r: 0, g: 0, b: 0 };
    return { r: (num >> 16) & 255, g: (num >> 8) & 255, b: num & 255 };
}

function toHex2(n) {
    return Math.max(0, Math.min(255, n)).toString(16).padStart(2, '0');
}

/** Interpolación lineal en RGB entre dos colores hex, t entre 0 y 1. Devuelve hex. */
function lerpColor(hexA, hexB, t) {
    const a = hexToRgb(hexA);
    const b = hexToRgb(hexB);
    const clamped = Math.max(0, Math.min(1, t));
    const r = Math.round(a.r + (b.r - a.r) * clamped);
    const g = Math.round(a.g + (b.g - a.g) * clamped);
    const bl = Math.round(a.b + (b.b - a.b) * clamped);
    return `#${toHex2(r)}${toHex2(g)}${toHex2(bl)}`;
}

/** Blanco o tinta oscura según la luminancia percibida del color de fondo. */
function textColorFor(hex) {
    const { r, g, b } = hexToRgb(hex);
    const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
    return luminance > 140 ? '#202124' : '#ffffff';
}

const TABLE_PAGE_SIZE = 25;
// Margen para que varios cambios de filtro seguidos salgan en un único lote.
const QUERY_DEBOUNCE_MS = 300;

function sameCellValue(a, b) {
    if (a == null && b == null) return true;
    if (typeof a === 'number' && typeof b === 'number') return a === b;
    return String(a ?? '') === String(b ?? '');
}

function computeMergedSpans(rows, columns) {
    const spans = rows.map(() => ({}));
    columns.forEach((col, colIdx) => {
        let i = 0;
        while (i < rows.length) {
            let j = i + 1;
            while (j < rows.length) {
                const parentsMatch = columns.slice(0, colIdx).every(parent => sameCellValue(rows[i][parent], rows[j][parent]));
                if (!parentsMatch || !sameCellValue(rows[i][col], rows[j][col])) break;
                j += 1;
            }
            const span = j - i;
            spans[i][col] = { show: true, span };
            for (let k = i + 1; k < j; k += 1) spans[k][col] = { show: false, span: 0 };
            i = j;
        }
    });
    return spans;
}

/** Tooltip del tema ejecutivo: tarjeta con la categoría y una fila por serie. */
function ExecTooltip({ active, payload, label, formatLabel, formatValue, labelOf }) {
    if (!active || !payload || payload.length === 0) return null;
    const heading = label != null && label !== '' ? label : payload[0]?.name;
    return (
        <div style={execStyles.tooltip}>
            {heading != null && heading !== '' && (
                <div style={execStyles.tooltipTitle}>{formatLabel ? formatLabel(heading) : heading}</div>
            )}
            {payload.map((item, idx) => {
                const key = item.dataKey || item.name;
                const swatch = item.color || item.payload?.fill || item.fill;
                return (
                    <div key={`${key}-${idx}`} style={execStyles.tooltipRow}>
                        <span style={{ ...execStyles.tooltipDot, backgroundColor: swatch }} />
                        <span style={execStyles.tooltipName}>{labelOf(key)}</span>
                        <span style={execStyles.tooltipValue}>{formatValue(item.value, key)}</span>
                    </div>
                );
            })}
        </div>
    );
}

/** Placeholder con brillo mientras llega el primer resultado del widget. */
function WidgetSkeleton({ type }) {
    if (type === 'kpi') {
        return (
            <div style={execStyles.skelKpi}>
                <div className="cm-skel" style={{ width: '38%', height: 10 }} />
                <div className="cm-skel" style={{ width: '62%', height: 26 }} />
            </div>
        );
    }
    if (type === 'table') {
        return (
            <div style={execStyles.skelTable}>
                {[0, 1, 2, 3, 4].map(row => (
                    <div key={row} className="cm-skel" style={{ height: row === 0 ? 14 : 10, width: row === 0 ? '100%' : `${92 - row * 6}%` }} />
                ))}
            </div>
        );
    }
    if (type === 'pie') {
        return (
            <div style={execStyles.skelCenter}>
                <div className="cm-skel" style={{ width: 110, height: 110, borderRadius: '50%' }} />
            </div>
        );
    }
    return (
        <div style={execStyles.skelBars}>
            {[46, 72, 58, 88, 64, 40, 76].map((h, idx) => (
                <div key={idx} className="cm-skel" style={{ flex: 1, height: `${h}%`, borderRadius: '4px 4px 0 0' }} />
            ))}
        </div>
    );
}

const WidgetRenderer = ({
    config,
    engineFilters = [],
    showTitle = true,
    filterValues = {},
    onFilterValueChange,
    schemaColumns = [],
    schemasBySource = {},
    filterDefs = [],
    dataSources = [],
    joins = [],
    onQueryPatch,
    exportable = false,
    nested = false,
    stacked = false,
    canvasRowHeight
}) => {
    const captureRef = useRef(null);
    const theme = useDashboardTheme();
    const exec = theme === 'executive';
    const [retryNonce, setRetryNonce] = useState(0);
    const [dataVersion, setDataVersion] = useState(currentEngineDataVersion);
    const [data, setData] = useState([]);
    // Datos que el motor devuelve además de las filas (comparación y tendencia del KPI).
    const [kpiInfo, setKpiInfo] = useState(null);
    // Total general del motor; null si el resultado viene recortado por Top N sin «Otros».
    const [engineTotals, setEngineTotals] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);
    const [sortKey, setSortKey] = useState(null);
    const [sortDir, setSortDir] = useState('asc');
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(TABLE_PAGE_SIZE);
    const tableRef = useRef(null);
    const mapContainerRef = useRef(null);
    const mapObserverRef = useRef(null);
    const [mapSize, setMapSize] = useState({ width: 0, height: 0 });
    const [mapHover, setMapHover] = useState(null);
    const [mapTransform, setMapTransform] = useState({ scale: 1, x: 0, y: 0 });
    const mapPanRef = useRef(null);
    // Callback ref en vez de useEffect+useRef: el contenedor solo existe en el DOM
    // cuando el mapa ya tiene dimensión+métrica (si no, se pinta el estado vacío),
    // así que un efecto atado al montaje del componente nunca vería el nodo real.
    const mapContainerCallbackRef = useCallback((node) => {
        if (mapObserverRef.current) {
            mapObserverRef.current.disconnect();
            mapObserverRef.current = null;
        }
        mapContainerRef.current = node;
        if (!node || typeof ResizeObserver === 'undefined') return;
        const observer = new ResizeObserver((entries) => {
            const entry = entries[0];
            if (!entry) return;
            const { width, height } = entry.contentRect;
            setMapSize(prev => (prev.width === width && prev.height === height ? prev : { width, height }));
        });
        observer.observe(node);
        mapObserverRef.current = observer;
    }, []);

    const query = config?.query || {};
    const [columnWidths, setColumnWidths] = useState(query.columnWidths || {});
    const widgetType = normalizeWidgetType(config?.type);
    const title = config?.title || '';
    const style = getWidgetStyle(config);
    const color = style.color || CHART_PALETTE[0];
    const canExport = exportable && style.showExport !== false;

    const hasQueryShape = widgetHasQueryShape(query);
    const catalog = useMemo(() => ({ dataSources, joins }), [dataSources, joins]);
    const scopedFilters = useMemo(
        () => (filterDefs.length
            ? buildEngineFilters(filterDefs, filterValues, query.dataSourceId, catalog)
            : engineFilters),
        [filterDefs, filterValues, query.dataSourceId, engineFilters, catalog]
    );
    const requestPayload = useMemo(
        () => buildWidgetQueryPayload(query, catalog, scopedFilters, config),
        [query, catalog, scopedFilters, config]
    );

    useEffect(() => {
        const applyResult = (res) => {
            setData(res.records || []);
            setKpiInfo(res.kpi || null);
            setEngineTotals(res.totals && !(res.limited && !res.others_count) ? res.totals : null);
            const bits = [];
            if (res.truncated) bits.push('Se muestran los primeros 10.000 registros.');
            const seen = new Set();
            (res.warnings || []).forEach((item) => {
                const text = typeof item === 'string' ? item : (item.reason || item.message);
                if (text && !seen.has(text)) {
                    seen.add(text);
                    bits.push(text);
                }
            });
            setNotice(bits.length ? bits.join(' ') : null);
            setError(null);
        };

        const runQuery = async (signal, quiet) => {
            if (widgetType === 'slicer' || isDecorationType(widgetType) || isContainerType(widgetType)) {
                setLoading(false);
                setError(null);
                setNotice(null);
                setData([]);
                return;
            }
            if (!widgetHasQueryShape(query)) {
                setLoading(false);
                setError(null);
                setNotice(null);
                setData([]);
                return;
            }
            const sourceMeta = (dataSources || []).find(item => item.id === (query.dataSourceId || query.data_source_id));
            const isJoinSource = (sourceMeta?.kind || '') === 'join';
            const sheetReady = Boolean(requestPayload.spreadsheet_id && requestPayload.sheet_name);
            if (!isJoinSource && !sheetReady && !requestPayload.data_source_id) {
                setError('El widget no tiene una fuente de datos configurada.');
                setData([]);
                return;
            }
            if (isJoinSource && !(requestPayload.data_sources || []).length) {
                setLoading(false);
                setError('La unión no tiene las hojas de origen. Ábrela en Fuentes y prueba la unión.');
                setData([]);
                return;
            }
            if (!isJoinSource && !sheetReady) {
                setLoading(false);
                setError('Conecta una hoja de cálculo en Fuentes de datos.');
                setData([]);
                return;
            }

            // Sin quiet se pinta «Consultando…»; con quiet ya hay filas en pantalla.
            if (!quiet) setLoading(true);
            setError(null);
            try {
                const res = await engineQuery(requestPayload, { signal });
                if (signal.aborted) return;
                applyResult(res);
                setPage(0);
            } catch (err) {
                if (err.name === 'AbortError') return;
                setError(err.message);
                setData([]);
            } finally {
                if (!signal.aborted) setLoading(false);
            }
        };

        const controller = new AbortController();
        // Un resultado ya visto (mismo payload) se pinta al instante y luego se revalida.
        const cached = peekQueryResult(requestPayload);
        if (cached) applyResult(cached);
        // Un snapshot de hace menos de 5 min ya es lo que diría el motor: no se revalida.
        if (cached && isQueryResultTrusted(requestPayload)) {
            setLoading(false);
            return undefined;
        }
        const timer = setTimeout(
            () => { runQuery(controller.signal, Boolean(cached)); },
            cached ? 0 : QUERY_DEBOUNCE_MS
        );
        return () => {
            clearTimeout(timer);
            controller.abort();
        };
    }, [JSON.stringify(requestPayload), widgetType, retryNonce, dataVersion]);

    // «Actualizar» y el refresco automático suben la versión: se reconsulta en sitio.
    useEffect(() => subscribeEngineData(setDataVersion), []);

    useEffect(() => {
        setColumnWidths(query.columnWidths || {});
    }, [JSON.stringify(query.columnWidths || {})]);

    const fmt = (val, key) => {
        const spec = parseMetricFormat(query.metricFormats?.[key], style.numberFormat);
        const decimals = spec.decimals ?? style.numberDecimals ?? null;
        return formatNumber(val, spec.type, decimals);
    };
    const labelOf = (name) => getAlias(query, name);
    // Fechas agrupadas por el motor se muestran como «mar 2026», «T1 2026», etc.
    const grainOf = (name) => query.dateGrains?.[name] || '';
    const formatDimValue = (name, value) => {
        const grain = grainOf(name);
        return grain ? grainLabelFormatter(grain)(value) : value;
    };
    const dimensionKeys = query.dimensions || [];
    const requestedMetrics = visibleMetricList(query);
    const dataKeys = data.length > 0 ? Object.keys(data[0]) : [];
    const metricKeys = requestedMetrics.filter(key => !dimensionKeys.includes(key) && (dataKeys.length === 0 || dataKeys.includes(key)));
    const headers = [...dimensionKeys, ...metricKeys].filter((key, idx, list) => list.indexOf(key) === idx && (dataKeys.length === 0 || dataKeys.includes(key)));
    const categoryKey = dimensionKeys[0] || null;
    const palette = exec ? EXEC_PALETTE : CHART_PALETTE;
    const seriesColor = (key, idx) => {
        if (style.seriesColors?.[key]) return style.seriesColors[key];
        if (key === OTHERS_LABEL) return '#c9ccd3';
        return idx === 0 ? color : palette[idx % palette.length];
    };

    const chartRows = useMemo(() => {
        if (!isChartType(widgetType)) return data;
        let rows = [...data];
        if (style.sortKey) {
            const sortField = style.sortKey;
            rows.sort((a, b) => {
                const av = a[sortField];
                const bv = b[sortField];
                if (typeof av === 'number' && typeof bv === 'number') {
                    return style.sortDir === 'asc' ? av - bv : bv - av;
                }
                return style.sortDir === 'asc'
                    ? String(av ?? '').localeCompare(String(bv ?? ''), 'es')
                    : String(bv ?? '').localeCompare(String(av ?? ''), 'es');
            });
        } else {
            // Sin orden elegido: fechas en orden cronológico; el resto por métrica.
            rows = defaultChartOrder(rows, categoryKey, metricKeys[0]);
            const timeline = categoryKey && isDateCategory(rows.map(row => row[categoryKey]));
            if (!timeline && style.sortDir === 'asc') rows.reverse();
        }
        // La fila «Otros» del motor va al final y no cuenta para el Top N.
        const split = splitOthers(rows);
        rows = split.rows;
        if (Number(style.topN) > 0) rows = rows.slice(0, Number(style.topN));
        return [...rows, ...split.others];
    }, [data, widgetType, style.sortKey, style.sortDir, style.topN, metricKeys, categoryKey]);

    const seriesTotals = useMemo(() => {
        const totals = {};
        (metricKeys || []).forEach((key) => {
            totals[key] = (chartRows || []).reduce((sum, row) => sum + (Number(row[key]) || 0), 0);
        });
        return totals;
    }, [chartRows, metricKeys]);

    const formatDataLabel = (value, key, row) => {
        const n = Number(value);
        const mode = style.labelFormat || (widgetType === 'pie' ? 'percent' : 'value');
        const rowTotal = (metricKeys || []).reduce((sum, metric) => sum + (Number(row?.[metric]) || 0), 0);
        const base = style.stacked ? rowTotal : (seriesTotals[key] || 0);
        const pct = Number.isFinite(n) && base > 0 ? (n / base) * 100 : 0;
        const pctText = `${pct.toFixed(pct >= 10 || pct === 0 ? 0 : 1)}%`;
        const valText = fmt(value, key);
        if (mode === 'percent') return pctText;
        if (mode === 'both') return `${valText} (${pctText})`;
        return valText;
    };

    useEffect(() => {
        /* Quitar el orden en Propiedades también quita el orden que la tabla tenía de antes. */
        setSortKey(style.sortKey || '');
        setSortDir(style.sortDir === 'asc' ? 'asc' : 'desc');
        setPage(0);
    }, [style.sortKey, style.sortDir]);

    const sortedRows = useMemo(() => {
        const key = sortKey || style.sortKey;
        const dir = sortDir || style.sortDir || 'desc';
        const split = splitOthers([...data]);
        let rows = split.rows;
        if (key) {
            rows.sort((a, b) => {
                const av = a[key];
                const bv = b[key];
                if (av == null && bv == null) return 0;
                if (av == null) return 1;
                if (bv == null) return -1;
                if (typeof av === 'number' && typeof bv === 'number') {
                    return dir === 'asc' ? av - bv : bv - av;
                }
                return dir === 'asc'
                    ? String(av).localeCompare(String(bv), 'es')
                    : String(bv).localeCompare(String(av), 'es');
            });
        }
        const limit = Number(style.topN);
        if (limit > 0) rows = rows.slice(0, limit);
        return [...rows, ...split.others];
    }, [data, sortKey, sortDir, style.sortKey, style.sortDir, style.topN]);

    const toggleSort = (key) => {
        if (sortKey === key) setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
        else { setSortKey(key); setSortDir('asc'); }
        setPage(0);
    };

    const renderChart = () => {
        if (!categoryKey || metricKeys.length === 0) {
            return <div style={styles.emptyState}>Arrastra una dimensión y una métrica al gráfico para visualizarlo.</div>;
        }

        const categoryValues = chartRows.map(row => row[categoryKey]);
        const dateLabel = grainOf(categoryKey)
            ? grainLabelFormatter(grainOf(categoryKey))
            : (exec && isDateCategory(categoryValues) ? dateCategoryFormatter(categoryValues) : null);
        const barLike = widgetType === 'bar' || widgetType === 'hbar' || widgetType === 'combo';
        const tooltip = exec ? (
            <Tooltip
                cursor={barLike ? { fill: 'rgba(94, 53, 177, 0.06)' } : { stroke: EXEC.line, strokeWidth: 1 }}
                content={<ExecTooltip formatLabel={dateLabel} formatValue={fmt} labelOf={labelOf} />}
            />
        ) : (
            <Tooltip
                formatter={(value, name, item) => {
                    const key = item?.dataKey || name;
                    return [fmt(value, key), labelOf(key)];
                }}
            />
        );
        // Ejecutivo: una sola serie no lleva leyenda (el título la nombra); la dona sí, por categorías.
        const wantsLegend = style.showLegend && (!exec || widgetType === 'pie' || metricKeys.length > 1);
        const legend = wantsLegend
            ? (exec ? (
                <Legend
                    verticalAlign={style.legendPosition === 'top' ? 'top' : 'bottom'}
                    iconType="circle"
                    iconSize={8}
                    wrapperStyle={{ fontSize: EXEC.fontSize, color: EXEC.muted, paddingTop: 4 }}
                    formatter={(value) => <span style={{ color: EXEC.muted }}>{dateLabel ? dateLabel(value) : value}</span>}
                />
            ) : <Legend verticalAlign={style.legendPosition === 'top' ? 'top' : 'bottom'} />)
            : null;

        const plotRows = chartRows.map((row) => {
            const next = { ...row };
            metricKeys.forEach((key) => {
                next[`${key}__label`] = formatDataLabel(row[key], key, row);
            });
            return next;
        });

        if (widgetType === 'pie') {
            const metric = metricKeys[0];
            if (exec) {
                // Dona con total al centro (HTML sobre la gráfica) y leyenda propia debajo o arriba:
                // así el centro de la dona coincide con el de su área. Las rebanadas chicas van a «Otros».
                const slices = foldPieSlices(plotRows, categoryKey, metric);
                const total = slices.reduce((sum, row) => sum + (Number(row[metric]) || 0), 0);
                const RAD = Math.PI / 180;
                const sliceLabel = ({ cx, cy, midAngle, outerRadius, percent, value, payload }) => {
                    if (!(percent >= 0.05)) return null;
                    const r = outerRadius + 12;
                    const x = cx + r * Math.cos(-midAngle * RAD);
                    const y = cy + r * Math.sin(-midAngle * RAD);
                    return (
                        <text x={x} y={y} fill={EXEC.muted} fontSize={11} textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central">
                            {formatDataLabel(value, metric, payload)}
                        </text>
                    );
                };
                const pieLegend = wantsLegend ? (
                    <div style={execStyles.pieLegend}>
                        {slices.map((row, idx) => (
                            <span key={`${row[categoryKey]}-${idx}`} style={execStyles.pieLegendItem}>
                                <span style={{ ...execStyles.tooltipDot, backgroundColor: seriesColor(row[categoryKey] || metric, idx) }} />
                                {String(row[categoryKey] ?? '')}
                            </span>
                        ))}
                    </div>
                ) : null;
                return (
                    <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                        {style.legendPosition === 'top' && pieLegend}
                        <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={slices}
                                        dataKey={metric}
                                        nameKey={categoryKey}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={style.innerRadius ? `${style.innerRadius}%` : '60%'}
                                        outerRadius={style.showLabels ? '74%' : '86%'}
                                        paddingAngle={slices.length > 1 ? 1.5 : 0}
                                        stroke={EXEC.surface}
                                        strokeWidth={2}
                                        labelLine={false}
                                        label={style.showLabels ? sliceLabel : false}
                                        isAnimationActive={false}
                                    >
                                        {slices.map((row, idx) => (
                                            <Cell key={idx} fill={seriesColor(row[categoryKey] || metric, idx)} />
                                        ))}
                                    </Pie>
                                    {tooltip}
                                </PieChart>
                            </ResponsiveContainer>
                            <div style={execStyles.pieCenter}>
                                <span style={execStyles.pieTotal}>{fmt(total, metric)}</span>
                                <span style={execStyles.pieTotalLabel}>Total</span>
                            </div>
                        </div>
                        {style.legendPosition !== 'top' && pieLegend}
                    </div>
                );
            }
            return (
                <div style={{ flex: 1, minHeight: 0 }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                            <Pie
                                data={plotRows}
                                dataKey={metric}
                                nameKey={categoryKey}
                                cx="50%"
                                cy="50%"
                                innerRadius={style.innerRadius ? `${style.innerRadius}%` : 0}
                                outerRadius="70%"
                                label={style.showLabels ? (entry) => `${entry.name} ${entry[`${metric}__label`] || formatDataLabel(entry[metric] ?? entry.value, metric, entry)}` : false}
                            >
                                {chartRows.map((row, idx) => (
                                    <Cell key={idx} fill={seriesColor(row[categoryKey] || metric, idx)} />
                                ))}
                            </Pie>
                            {tooltip}
                            {legend}
                        </PieChart>
                    </ResponsiveContainer>
                </div>
            );
        }

        const horizontal = widgetType === 'hbar';
        const useArea = widgetType === 'area' || (widgetType === 'line' && style.fillArea);
        const ChartComponent = widgetType === 'combo'
            ? ComposedChart
            : (useArea ? AreaChart : (widgetType === 'line' ? LineChart : BarChart));
        const lineMetrics = new Set(style.comboLineMetrics || []);
        const axisFmt = (val) => fmt(val, metricKeys[0]);
        const catAngle = Number(style.categoryTickAngle) || 0;
        const catFont = Number(style.categoryTickSize) || 12;
        const valFont = Number(style.valueTickSize) || 12;
        const showCategoryAxis = style.showCategoryAxis !== false;
        const showValueAxis = style.showValueAxis !== false;
        const showCategoryTicks = style.showCategoryTicks !== false;
        const showValueTicks = style.showValueTicks !== false;
        const catMaxChars = Number(style.categoryTickMaxChars);
        const formatCategoryTick = (val) => {
            const text = String(dateLabel ? dateLabel(val) : (val ?? ''));
            if (Number.isFinite(catMaxChars) && catMaxChars > 0 && text.length > catMaxChars) {
                return `${text.slice(0, catMaxChars)}…`;
            }
            return text;
        };
        const formatValueTick = (val) => {
            if (typeof val !== 'number') return val;
            const format = style.valueAxisFormat || 'inherit';
            if (exec && (!format || format === 'inherit')) return compactNumber(val);
            if (!format || format === 'inherit') return axisFmt(val);
            return formatNumber(val, format, style.valueAxisDecimals ?? null);
        };
        const parseAxisBound = (raw) => {
            if (raw === '' || raw == null) return 'auto';
            const n = Number(raw);
            return Number.isFinite(n) ? n : 'auto';
        };
        const valueDomain = [parseAxisBound(style.valueAxisMin), parseAxisBound(style.valueAxisMax)];
        const gradientId = (key) => `cm-grad-${String(config?.id || 'w').replace(/[^\w-]/g, '')}-${String(key).replace(/[^\w-]/g, '_')}`;
        const labelStyle = exec ? { fill: EXEC.muted, fontSize: 11 } : undefined;
        // Marcador del ejecutivo: relleno del color de la serie con anillo del color de la superficie.
        const lineDot = (fill) => (style.showDots ? (exec ? { r: 3.5, strokeWidth: 2, stroke: EXEC.surface, fill } : true) : false);
        const activeDot = exec ? { r: 5, strokeWidth: 2, stroke: EXEC.surface } : undefined;
        const catInterval = style.categoryTickInterval === 'all'
            ? 0
            : (style.categoryTickInterval === 'ends' ? 'preserveStartEnd' : 'preserveEnd');
        const catTitle = style.xAxisLabel
            ? { value: style.xAxisLabel, position: horizontal ? 'insideLeft' : 'insideBottom', angle: horizontal ? -90 : 0, offset: horizontal ? 10 : -4 }
            : undefined;
        const valTitle = style.yAxisLabel
            ? { value: style.yAxisLabel, position: horizontal ? 'insideBottom' : 'insideLeft', angle: horizontal ? 0 : -90, offset: horizontal ? -2 : 0 }
            : undefined;
        const chartMargin = {
            top: style.legendPosition === 'top' ? 28 : 8,
            right: showValueTicks && horizontal ? 28 : 16,
            left: showValueAxis && !horizontal ? 8 : 4,
            bottom: (style.xAxisLabel ? 24 : 8) + (catAngle && !horizontal ? 28 : 0)
        };
        const tickFill = exec ? EXEC.muted : '#5f6368';
        const categoryAxis = {
            hide: !showCategoryAxis,
            tick: showCategoryTicks ? { fontSize: catFont, fill: tickFill } : false,
            tickLine: exec ? false : showCategoryTicks,
            axisLine: exec ? (showCategoryAxis ? { stroke: EXEC.line } : false) : showCategoryAxis,
            interval: catInterval,
            tickFormatter: formatCategoryTick,
            label: catTitle
        };
        const valueAxis = {
            hide: !showValueAxis,
            tick: showValueTicks ? { fontSize: valFont, fill: tickFill } : false,
            tickLine: exec ? false : showValueTicks,
            axisLine: exec ? false : showValueAxis,
            tickFormatter: formatValueTick,
            domain: valueDomain,
            allowDecimals: true,
            label: valTitle
        };

        return (
            <div style={{ flex: 1, minHeight: 0 }}>
                <ResponsiveContainer width="100%" height="100%">
                    <ChartComponent data={plotRows} layout={horizontal ? 'vertical' : undefined} margin={chartMargin}>
                        {style.showGrid && (exec
                            ? <CartesianGrid stroke={EXEC.grid} vertical={horizontal} horizontal={!horizontal} />
                            : <CartesianGrid strokeDasharray="3 3" />)}
                        {exec && useArea && (
                            <defs>
                                {metricKeys.map((key, idx) => (
                                    <linearGradient key={key} id={gradientId(key)} x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor={seriesColor(key, idx)} stopOpacity={0.28} />
                                        <stop offset="100%" stopColor={seriesColor(key, idx)} stopOpacity={0.02} />
                                    </linearGradient>
                                ))}
                            </defs>
                        )}
                        {horizontal ? (
                            <>
                                <XAxis type="number" {...valueAxis} />
                                <YAxis type="category" dataKey={categoryKey} width={showCategoryTicks ? Math.max(64, catFont * 5) : 16} {...categoryAxis} />
                            </>
                        ) : (
                            <>
                                <XAxis
                                    dataKey={categoryKey}
                                    angle={catAngle}
                                    textAnchor={catAngle ? 'end' : 'middle'}
                                    height={catAngle ? 64 : 30}
                                    {...categoryAxis}
                                />
                                <YAxis width={showValueTicks ? Math.max(40, valFont * 4) : 16} {...valueAxis} />
                            </>
                        )}
                        {tooltip}
                        {legend}
                        {metricKeys.map((key, idx) => {
                            const fill = seriesColor(key, idx);
                            const asLine = widgetType === 'combo' && (lineMetrics.size === 0 ? idx === metricKeys.length - 1 : lineMetrics.has(key));
                            if (widgetType === 'line' && useArea) {
                                return (
                                    <Area
                                        key={key}
                                        name={labelOf(key)}
                                        type="monotone"
                                        dataKey={key}
                                        stroke={fill}
                                        fill={exec ? `url(#${gradientId(key)})` : fill}
                                        fillOpacity={exec ? 1 : 0.25}
                                        strokeWidth={style.strokeWidth}
                                        dot={lineDot(fill)}
                                        activeDot={activeDot}
                                    />
                                );
                            }
                            if (widgetType === 'area') {
                                return (
                                    <Area
                                        key={key}
                                        name={labelOf(key)}
                                        type="monotone"
                                        dataKey={key}
                                        stroke={fill}
                                        fill={exec ? `url(#${gradientId(key)})` : fill}
                                        fillOpacity={exec ? 1 : 0.35}
                                        strokeWidth={style.strokeWidth}
                                        stackId={style.stacked ? 'stack' : undefined}
                                        activeDot={activeDot}
                                    />
                                );
                            }
                            if (widgetType === 'line' || asLine) {
                                return (
                                    <Line
                                        key={key}
                                        name={labelOf(key)}
                                        type="monotone"
                                        dataKey={key}
                                        stroke={fill}
                                        strokeWidth={style.strokeWidth}
                                        strokeLinecap="round"
                                        dot={lineDot(fill)}
                                        activeDot={activeDot}
                                        yAxisId={0}
                                        label={style.showLabels ? <LabelList dataKey={`${key}__label`} position="top" style={labelStyle} /> : undefined}
                                    />
                                );
                            }
                            // Apiladas: solo el segmento superior lleva la punta redondeada.
                            const topOfStack = !style.stacked || idx === metricKeys.length - 1;
                            const r = topOfStack ? style.barRadius : 0;
                            return (
                                <Bar
                                    key={key}
                                    name={labelOf(key)}
                                    dataKey={key}
                                    fill={fill}
                                    stackId={style.stacked ? 'stack' : undefined}
                                    maxBarSize={exec ? EXEC_MAX_BAR : undefined}
                                    stroke={exec && style.stacked ? EXEC.surface : undefined}
                                    strokeWidth={exec && style.stacked ? 1 : undefined}
                                    radius={horizontal ? [0, r, r, 0] : [r, r, 0, 0]}
                                >
                                    {plotRows.some(isOthersRow) && plotRows.map((row, i) => (
                                        <Cell key={`c-${i}`} fill={isOthersRow(row) ? '#c9ccd3' : fill} />
                                    ))}
                                    {style.showLabels && (
                                        <LabelList
                                            dataKey={`${key}__label`}
                                            position={horizontal ? 'right' : 'top'}
                                            style={labelStyle}
                                        />
                                    )}
                                </Bar>
                            );
                        })}
                    </ChartComponent>
                </ResponsiveContainer>
            </div>
        );
    };

    const renderMap = () => {
        if (!categoryKey || metricKeys.length === 0) {
            return <div style={styles.emptyState}>Arrastra una dimensión de estados y una métrica al mapa.</div>;
        }
        const metric = metricKeys[0];
        const valuesByStateId = new Map();
        chartRows.forEach((row) => {
            const { feature, matched } = matchStateFeature(row[categoryKey]);
            if (!matched) return;
            const id = feature.properties.id;
            valuesByStateId.set(id, (valuesByStateId.get(id) || 0) + (Number(row[metric]) || 0));
        });
        const numbers = [...valuesByStateId.values()];
        const min = numbers.length ? Math.min(...numbers) : 0;
        const max = numbers.length ? Math.max(...numbers) : 0;
        const noDataColor = style.noDataColor || MAP_NO_DATA_COLOR;
        const colorMin = style.mapColorMin || MAP_COLOR_MIN_DEFAULT;
        const colorMax = style.mapColorMax || MAP_COLOR_MAX_DEFAULT;
        const colorForValue = (value) => {
            if (value == null) return noDataColor;
            if (max === min) return colorMax;
            return lerpColor(colorMin, colorMax, (value - min) / (max - min));
        };

        const enableZoom = style.enableZoom !== false;
        const showLabels = Boolean(style.showMapLabels);
        const labelContent = style.mapLabelContent || 'name';
        const labelText = (name, value) => {
            if (labelContent === 'value') return value == null ? null : fmt(value, metric);
            if (labelContent === 'both') return value == null ? name : `${name}: ${fmt(value, metric)}`;
            return name;
        };

        const { width, height } = mapSize;
        const pathGenerator = width > 0 && height > 0
            ? geoPath(geoMercator().fitSize([width, height], MEXICO_STATES_GEOJSON))
            : null;
        const { scale, x: panX, y: panY } = mapTransform;
        const zoomedIn = scale > MAP_ZOOM_MIN;

        const hoverAt = (event, feature, value) => {
            if (mapPanRef.current) return;
            const rect = mapContainerRef.current?.getBoundingClientRect();
            setMapHover({
                name: feature.properties.name,
                value,
                x: rect ? event.clientX - rect.left : 0,
                y: rect ? event.clientY - rect.top : 0
            });
        };

        const zoomBy = (factor) => {
            setMapTransform(prev => {
                const nextScale = Math.max(MAP_ZOOM_MIN, Math.min(MAP_ZOOM_MAX, prev.scale * factor));
                if (nextScale === prev.scale) return prev;
                if (nextScale === MAP_ZOOM_MIN) return { scale: MAP_ZOOM_MIN, x: 0, y: 0 };
                const cx = width / 2;
                const cy = height / 2;
                const ratio = nextScale / prev.scale;
                return { scale: nextScale, x: cx - (cx - prev.x) * ratio, y: cy - (cy - prev.y) * ratio };
            });
        };
        const resetZoom = () => setMapTransform({ scale: MAP_ZOOM_MIN, x: 0, y: 0 });

        const onPanStart = (event) => {
            if (!enableZoom || scale <= MAP_ZOOM_MIN) return;
            event.preventDefault();
            const startX = event.clientX;
            const startY = event.clientY;
            const origX = panX;
            const origY = panY;
            mapPanRef.current = true;
            setMapHover(null);
            const onMove = (moveEvent) => {
                setMapTransform(prev => ({ ...prev, x: origX + (moveEvent.clientX - startX), y: origY + (moveEvent.clientY - startY) }));
            };
            const onUp = () => {
                mapPanRef.current = null;
                window.removeEventListener('mousemove', onMove);
                window.removeEventListener('mouseup', onUp);
            };
            window.addEventListener('mousemove', onMove);
            window.addEventListener('mouseup', onUp);
        };

        return (
            <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div
                    ref={mapContainerCallbackRef}
                    style={{ flex: 1, minHeight: 0, position: 'relative' }}
                    onMouseLeave={() => setMapHover(null)}
                >
                    {pathGenerator && (
                        <svg
                            width={width}
                            height={height}
                            style={{ display: 'block', cursor: enableZoom && zoomedIn ? 'grab' : 'default' }}
                            onMouseDown={onPanStart}
                        >
                            <g transform={`translate(${panX}, ${panY}) scale(${scale})`}>
                                {MEXICO_STATES_GEOJSON.features.map((feature) => {
                                    const value = valuesByStateId.get(feature.properties.id);
                                    const fill = colorForValue(value);
                                    const text = showLabels ? labelText(feature.properties.name, value) : null;
                                    const [cx, cy] = showLabels ? pathGenerator.centroid(feature) : [0, 0];
                                    return (
                                        <React.Fragment key={feature.properties.id}>
                                            <path
                                                d={pathGenerator(feature)}
                                                fill={fill}
                                                stroke="#ffffff"
                                                strokeWidth={0.75 / scale}
                                                onMouseMove={(event) => hoverAt(event, feature, value)}
                                            />
                                            {text && (
                                                <text
                                                    x={cx}
                                                    y={cy}
                                                    textAnchor="middle"
                                                    dominantBaseline="middle"
                                                    style={{ pointerEvents: 'none', fontSize: 7, fontWeight: 600 }}
                                                    fill={textColorFor(fill)}
                                                >
                                                    {text}
                                                </text>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </g>
                        </svg>
                    )}
                    {enableZoom && pathGenerator && (
                        <div style={styles.mapZoomControls}>
                            <button type="button" style={styles.mapZoomBtn} onClick={() => zoomBy(MAP_ZOOM_STEP)} title="Acercar">+</button>
                            <button type="button" style={styles.mapZoomBtn} onClick={() => zoomBy(1 / MAP_ZOOM_STEP)} title="Alejar">−</button>
                            <button
                                type="button"
                                style={{ ...styles.mapZoomBtn, opacity: zoomedIn ? 1 : 0.4 }}
                                onClick={resetZoom}
                                disabled={!zoomedIn}
                                title="Restablecer"
                            >⟲</button>
                        </div>
                    )}
                    {mapHover && (
                        <div style={{ ...styles.mapTooltip, left: mapHover.x + 12, top: mapHover.y + 12 }}>
                            <strong>{mapHover.name}</strong>
                            <div>{mapHover.value == null ? 'Sin datos' : fmt(mapHover.value, metric)}</div>
                        </div>
                    )}
                </div>
                {style.showLegend !== false && (
                    <div style={styles.mapLegend}>
                        <div style={{
                            ...styles.mapLegendBar,
                            backgroundImage: `linear-gradient(to right, ${colorMin}, ${colorMax})`
                        }} />
                        <div style={styles.mapLegendLabels}>
                            <span>{fmt(min, metric)}</span>
                            <span>{fmt(max, metric)}</span>
                        </div>
                        <div style={styles.mapLegendNoData}>
                            <span style={{ ...styles.mapLegendSwatch, backgroundColor: noDataColor }} />
                            Sin datos
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const renderKpi = () => {
        if (data.length === 0 || metricKeys.length === 0) {
            return <div style={styles.emptyState}>Arrastra una métrica al KPI.</div>;
        }
        const row = data[0];
        const kpiInk = themed(color, '#5E35B1', EXEC.ink, exec);
        const kpiCfg = query.kpi || {};
        const info = kpiInfo || {};
        // Sin filtro de fechas, el motor compara el último periodo y ese periodo es el que se muestra.
        const current = info.replaces_value && info.current ? info.current : row;
        const grainLabel = info.grain ? grainLabelFormatter(info.grain) : null;
        const periodCaption = info.replaces_value && info.current_range && grainLabel ? grainLabel(info.current_range[0]) : '';
        const versusText = !info.compare ? ''
            : info.compare === 'previous_year' ? 'vs año anterior'
                : (info.replaces_value && grainLabel && info.previous_range ? `vs ${grainLabel(info.previous_range[0])}` : 'vs periodo anterior');
        const betterDown = kpiCfg.better === 'down';
        const sparkKey = info.date_field;
        const targetFor = (values) => {
            if (kpiCfg.targetMode === 'value') {
                const n = Number(kpiCfg.targetValue);
                return Number.isFinite(n) && n > 0 ? n : null;
            }
            if (kpiCfg.targetMode === 'column') {
                const n = Number(values?.[KPI_TARGET_KEY]);
                return Number.isFinite(n) && n > 0 ? n : null;
            }
            return null;
        };
        return (
            <div style={{
                ...styles.kpiRow,
                ...(nested ? styles.kpiRowTop : {})
            }}>
                {metricKeys.map((key, keyIdx) => {
                    const value = current?.[key];
                    const cf = firstConditionalStyle(query.conditionalFormats?.[key], value);
                    const prev = info.previous ? info.previous[key] : undefined;
                    const hasCompare = Boolean(info.compare) && info.previous;
                    const delta = hasCompare && Number.isFinite(Number(prev)) && Number(prev) !== 0 && Number.isFinite(Number(value))
                        ? (Number(value) - Number(prev)) / Math.abs(Number(prev))
                        : null;
                    const good = delta == null || delta === 0 ? null : (betterDown ? delta < 0 : delta > 0);
                    const target = targetFor(current);
                    const progress = target && Number.isFinite(Number(value)) ? Number(value) / target : null;
                    const accent = seriesColor(key, keyIdx);
                    const spark = sparkKey && Array.isArray(info.spark)
                        ? info.spark.filter(point => typeof point[key] === 'number')
                        : [];
                    return (
                    <div key={key} style={{ ...styles.kpiCard, ...(spark.length > 1 || progress != null ? kpiStyles.cardWide : {}) }}>
                        {exec && style.showTitle !== false && (
                            <div style={execStyles.kpiLabel}>
                                {labelOf(key)}{periodCaption ? <span style={kpiStyles.period}> · {periodCaption}</span> : null}
                            </div>
                        )}
                        <div style={{
                            ...styles.kpiValue,
                            ...(exec ? execStyles.kpiValue : {}),
                            color: cf.color || kpiInk,
                            background: cf.background,
                            fontWeight: cf.fontWeight || (exec ? 600 : 700),
                            fontSize: `${style.kpiFontSize}px`,
                            borderRadius: cf.background ? 8 : 0,
                            padding: cf.background ? '4px 8px' : 0
                        }}>
                            {style.kpiPrefix}{fmt(value, key)}{style.kpiSuffix}
                        </div>
                        {!exec && style.showTitle !== false && (
                            <div style={styles.kpiLabel}>
                                {labelOf(key)}{periodCaption ? ` · ${periodCaption}` : ''}
                            </div>
                        )}
                        {hasCompare && (
                            delta == null ? (
                                <div style={{ ...kpiStyles.delta, ...kpiStyles.deltaNeutral }}>Sin datos {versusText}</div>
                            ) : (
                                <div
                                    style={{ ...kpiStyles.delta, ...(good == null ? kpiStyles.deltaNeutral : (good ? kpiStyles.deltaGood : kpiStyles.deltaBad)) }}
                                    title={`${versusText}: ${fmt(prev, key)}`}
                                >
                                    <span aria-hidden="true">{delta > 0 ? '▲' : (delta < 0 ? '▼' : '■')}</span>
                                    <span>{`${Math.abs(delta * 100).toFixed(Math.abs(delta) >= 0.1 ? 0 : 1)}%`}</span>
                                    <span style={kpiStyles.versus}>{versusText}</span>
                                </div>
                            )
                        )}
                        {progress != null && (
                            <div style={kpiStyles.target} title={`Meta: ${fmt(target, key)}`}>
                                <div style={kpiStyles.targetTrack}>
                                    <div style={{
                                        ...kpiStyles.targetFill,
                                        width: `${Math.max(0, Math.min(100, progress * 100))}%`,
                                        backgroundColor: progress >= 1 ? '#137333' : accent
                                    }} />
                                </div>
                                <span style={kpiStyles.targetText}>
                                    {`${Math.round(progress * 100)}% de la meta · ${fmt(target, key)}`}
                                </span>
                            </div>
                        )}
                        {spark.length > 1 && (
                            <div style={kpiStyles.spark} title={`Tendencia de ${labelOf(key)}`}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <AreaChart data={spark} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
                                        <defs>
                                            <linearGradient id={`kpi-spark-${String(config?.id || 'k').replace(/[^\w-]/g, '')}-${keyIdx}`} x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%" stopColor={accent} stopOpacity={0.25} />
                                                <stop offset="100%" stopColor={accent} stopOpacity={0.02} />
                                            </linearGradient>
                                        </defs>
                                        <Area
                                            type="monotone"
                                            dataKey={key}
                                            stroke={accent}
                                            strokeWidth={2}
                                            fill={`url(#kpi-spark-${String(config?.id || 'k').replace(/[^\w-]/g, '')}-${keyIdx})`}
                                            dot={false}
                                            isAnimationActive={false}
                                        />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>
                        )}
                    </div>
                    );
                })}
            </div>
        );
    };

    const renderSlicer = () => {
        const column = query.dimensions?.[0];
        if (!column) {
            return <div style={styles.emptyState}>Arrastra una dimensión a este filtro para convertirlo en lista desplegable.</div>;
        }
        const colMeta = schemaColumns.find(item => item.name === column);
        return (
            <div style={{
                ...styles.slicerBox,
                ...(nested ? styles.slicerBoxTop : {})
            }}>
                <FilterDropdown
                    label={(showTitle && style.showTitle !== false) ? (title || labelOf(column)) : ''}
                    value={filterValues[config.filterId] || []}
                    options={colMeta?.values || []}
                    column={column}
                    dataSource={{
                        ...sourceRequestFields(query, catalog),
                        spreadsheet_id: query.spreadsheet_id,
                        sheet_name: query.sheet_name
                    }}
                    onChange={(next) => onFilterValueChange && onFilterValueChange(config.filterId, next)}
                />
            </div>
        );
    };

    const renderTable = () => {
        if (!hasQueryShape) {
            return <div style={styles.emptyState}>Arrastra dimensiones y métricas a la tabla para visualizarla.</div>;
        }
        if (data.length === 0) {
            return <div style={styles.emptyState}>No hay registros para mostrar.</div>;
        }
        const compact = style.tableDensity === 'compact';
        const cellPad = compact ? '6px 10px' : '10px 12px';
        const headerFontSize = `${style.headerFontSize || (compact ? 12 : 13)}px`;
        const bodyFontSize = `${style.bodyFontSize || (compact ? 12 : 13)}px`;
        const totalsFontSize = `${style.totalsFontSize || style.bodyFontSize || (compact ? 12 : 13)}px`;
        const tableFont = !style.tableFontFamily || style.tableFontFamily === 'inherit' ? 'inherit' : style.tableFontFamily;
        // Ejecutivo: los colores que siguen en su default clásico pasan a la versión sobria.
        const headerBg = themed(style.headerBackground, CLASSIC_TABLE_DEFAULTS.headerBackground, EXEC.headerBg, exec);
        const headerFg = themed(style.headerColor, CLASSIC_TABLE_DEFAULTS.headerColor, EXEC.muted, exec);
        const bodyBg = style.bodyBackground || '#ffffff';
        const stripeBg = themed(style.stripeBackground, CLASSIC_TABLE_DEFAULTS.stripeBackground, EXEC.stripe, exec);
        const bodyFg = style.bodyColor || '#202124';
        const dimFg = themed(style.dimTextColor, CLASSIC_TABLE_DEFAULTS.dimTextColor, EXEC.ink, exec);
        const totalsBg = themed(style.totalsBackground, CLASSIC_TABLE_DEFAULTS.totalsBackground, EXEC.headerBg, exec);
        const totalsFg = themed(style.totalsColor, CLASSIC_TABLE_DEFAULTS.totalsColor, EXEC.ink, exec);
        const execRule = exec ? `1px solid ${EXEC.line}` : undefined;
        const alignOf = (mode, fallback) => (mode && mode !== 'auto' ? mode : fallback);
        const paginate = style.tablePaginate !== false;
        const pageCount = Math.max(1, Math.ceil(sortedRows.length / pageSize));
        const safePage = Math.min(page, pageCount - 1);
        const slice = paginate
            ? sortedRows.slice(safePage * pageSize, (safePage + 1) * pageSize)
            : sortedRows;
        const mergeByMetric = sortKey && metricKeys.includes(sortKey);
        const mergeDims = (style.mergeRepeatedCells !== false && !mergeByMetric)
            ? dimensionKeys.filter(key => headers.includes(key))
            : [];
        const mergedSpans = mergeDims.length ? computeMergedSpans(slice, mergeDims) : [];
        const calcFields = [...(query.agg_calculated_fields || []), ...(query.row_calculated_fields || [])];
        const formulaNames = new Set(calcFields.map(item => item.name));
        const totalModeOf = (header) => query.totalAggs?.[header] || (formulaNames.has(header) ? 'formula' : 'auto');
        const engineTotalOf = (header) => {
            const value = engineTotals?.[header];
            return typeof value === 'number' ? value : null;
        };
        const sumNums = (header) => {
            const nums = sortedRows.map(row => row[header]).filter(val => typeof val === 'number');
            if (nums.length === 0) return null;
            return nums.reduce((acc, n) => acc + n, 0);
        };
        const totals = {};
        metricKeys.forEach(header => {
            const mode = totalModeOf(header);
            if (mode === 'hide' || mode === 'formula') return;
            if (mode === 'auto' && engineTotalOf(header) != null) {
                totals[header] = engineTotalOf(header);
                return;
            }
            const nums = sortedRows.map(row => row[header]).filter(val => typeof val === 'number');
            if (nums.length === 0) return;
            if (mode === 'mean') totals[header] = nums.reduce((acc, n) => acc + n, 0) / nums.length;
            else if (mode === 'max') totals[header] = Math.max(...nums);
            else if (mode === 'min') totals[header] = Math.min(...nums);
            else totals[header] = nums.reduce((acc, n) => acc + n, 0);
        });
        headers.forEach(header => {
            if (totals[header] != null || totalModeOf(header) === 'hide' || totalModeOf(header) === 'formula') return;
            const summed = sumNums(header);
            if (summed != null) totals[header] = summed;
        });
        dataKeys.filter(key => String(key).startsWith('__agg_')).forEach(header => {
            if (totals[header] != null) return;
            const summed = sumNums(header);
            if (summed != null) totals[header] = summed;
        });
        metricKeys.forEach(header => {
            if (totalModeOf(header) !== 'formula') return;
            // Sin fórmula de total propia, el motor ya recalculó el campo sobre todas las filas.
            if (!query.totalFormulas?.[header] && engineTotalOf(header) != null) {
                totals[header] = engineTotalOf(header);
                return;
            }
            const calc = calcFields.find(item => item.name === header);
            const expr = query.totalFormulas?.[header] || calc?.formula;
            const value = evalNumericFormula(expr, totals);
            if (value != null) totals[header] = value;
        });

        const stickyFirst = (idx) => style.freezeFirstColumn && idx === 0
            ? { position: 'sticky', left: 0, zIndex: 1 }
            : {};
        /* Con algún ancho fijado la tabla usa layout fijo: las columnas sin ancho (p. ej. una métrica
           recién agregada) reciben 120 px; si no, el layout fijo las dejaba en 0 px y no se veían. */
        /* «Ajustar al ancho» (por defecto): la tabla ocupa el ancho del bloque sin barra horizontal y
           los anchos guardados se usan como proporción. «Desplazamiento»: anchos en px y scroll. */
        const fitWidth = style.tableWidthMode !== 'scroll';
        const anyLocked = headers.some(header => columnWidths[header]);
        const tablePixelWidth = !fitWidth && anyLocked
            ? headers.reduce((sum, header) => sum + Number(columnWidths[header] || 120), 0)
            : null;
        const fitWeight = header => Number(columnWidths[header]) || (dimensionKeys.includes(header) ? 160 : 110);
        const fitTotal = headers.reduce((sum, header) => sum + fitWeight(header), 0) || 1;
        const colWidth = (header) => {
            if (fitWidth) return {};
            const width = columnWidths[header];
            return width ? { width, minWidth: width, maxWidth: width } : { minWidth: 80 };
        };
        const snapshotColumnWidths = () => {
            const next = { ...columnWidths };
            const cells = tableRef.current?.querySelectorAll('thead th');
            headers.forEach((header, index) => {
                if (next[header] && !fitWidth) return;
                const measured = cells?.[index]?.getBoundingClientRect().width;
                next[header] = Math.max(48, Math.round(measured || 120));
            });
            return next;
        };
        const resizeColumn = (name, nextWidth, persist, locked) => {
            const base = locked || columnWidths;
            const next = { ...base, [name]: Math.max(48, Math.round(nextWidth)) };
            if (fitWidth) {
                /* Ajustada al ancho: lo que gana la columna lo cede la de la derecha (la suma no cambia). */
                const neighbor = headers[headers.indexOf(name) + 1];
                if (neighbor) {
                    const pool = Number(base[name] || 0) + Number(base[neighbor] || 0);
                    next[name] = Math.min(Math.max(48, Math.round(nextWidth)), pool - 48);
                    next[neighbor] = pool - next[name];
                }
            }
            setColumnWidths(next);
            if (persist && onQueryPatch) onQueryPatch({ columnWidths: next });
        };
        const cellText = (header, value, numeric) => (numeric ? fmt(value, header) : (formatDimValue(header, value) ?? '—'));
        const headerWrap = style.headerWrap !== false;
        /* Ajuste de título: corta solo entre palabras (overflow-wrap: break-word no reduce el ancho
           mínimo de la columna, a diferencia de `anywhere`, que la dejaba de una letra y el encabezado
           crecía en vertical hasta tapar la tabla) y se limita a 3 líneas; el texto completo va en title. */
        const headerTextStyle = headerWrap
            ? {
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 3,
                overflow: 'hidden',
                whiteSpace: 'pre-line',
                wordBreak: 'normal',
                overflowWrap: 'break-word',
                lineHeight: 1.25
            }
            : { display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.25 };

        return (
            <div style={styles.tableShell}>
                <div style={{
                    ...(fitWidth ? styles.tableWrapFit : styles.tableWrap),
                    ...(exec ? { border: `1px solid ${EXEC.line}` } : {})
                }}>
                    <table
                        ref={tableRef}
                        style={{
                            ...styles.table,
                            fontFamily: tableFont,
                            width: tablePixelWidth || '100%',
                            minWidth: fitWidth ? 0 : (tablePixelWidth || '100%'),
                            tableLayout: fitWidth || tablePixelWidth ? 'fixed' : 'auto'
                        }}
                    >
                        {fitWidth ? (
                            <colgroup>
                                {headers.map(header => (
                                    <col key={header} style={{ width: `${(fitWeight(header) / fitTotal) * 100}%` }} />
                                ))}
                            </colgroup>
                        ) : tablePixelWidth ? (
                            <colgroup>
                                {headers.map(header => (
                                    <col key={header} style={{ width: columnWidths[header] || 120, minWidth: columnWidths[header] || 120 }} />
                                ))}
                            </colgroup>
                        ) : null}
                        {style.showHeader !== false && (
                        <thead>
                            <tr>
                                {headers.map((header, idx) => (
                                    <ResizableTh
                                        key={header}
                                        header={header}
                                        width={fitWidth ? undefined : columnWidths[header]}
                                        onResizeStart={snapshotColumnWidths}
                                        onResize={resizeColumn}
                                        onClick={() => toggleSort(header)}
                                        style={{
                                            ...styles.th,
                                            ...colWidth(header),
                                            padding: cellPad,
                                            fontSize: headerFontSize,
                                            fontWeight: style.headerFontWeight || 600,
                                            fontStyle: style.headerItalic ? 'italic' : 'normal',
                                            textAlign: alignOf(style.headerAlign, metricKeys.includes(header) ? 'right' : 'left'),
                                            whiteSpace: headerWrap ? 'normal' : 'nowrap',
                                            overflow: 'hidden',
                                            verticalAlign: 'bottom',
                                            ...stickyFirst(idx),
                                            background: headerBg,
                                            color: headerFg,
                                            ...(exec ? { borderBottom: execRule, letterSpacing: 0 } : {}),
                                            zIndex: idx === 0 && style.freezeFirstColumn ? 3 : 2
                                        }}
                                    >
                                        <span style={headerTextStyle} title={labelOf(header)}>
                                            {labelOf(header)}
                                            {sortKey === header ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
                                        </span>
                                    </ResizableTh>
                                ))}
                            </tr>
                        </thead>
                        )}
                        <tbody>
                            {slice.map((row, rowIdx) => (
                                <tr key={rowIdx} style={{
                                    background: style.striped && rowIdx % 2 === 1 ? stripeBg : bodyBg
                                }}>
                                    {headers.map((header, idx) => {
                                        const spanInfo = mergedSpans[rowIdx]?.[header];
                                        if (spanInfo && !spanInfo.show) return null;
                                        /* Una dimensión numérica (año, folio) se muestra tal cual, sin formato de métrica. */
                                        const numeric = typeof row[header] === 'number' && !dimensionKeys.includes(header);
                                        const rowSpan = spanInfo?.span > 1 ? spanInfo.span : undefined;
                                        const merged = Boolean(rowSpan);
                                        const stripe = style.striped && rowIdx % 2 === 1 ? stripeBg : bodyBg;
                                        const cf = firstConditionalStyle(query.conditionalFormats?.[header], row[header]);
                                        return (
                                            <td
                                                key={header}
                                                rowSpan={rowSpan}
                                                title={fitWidth ? String(cellText(header, row[header], numeric)) : undefined}
                                                style={{
                                                    ...styles.td,
                                                    ...colWidth(header),
                                                    padding: cellPad,
                                                    fontSize: bodyFontSize,
                                                    fontFamily: tableFont,
                                                    textAlign: alignOf(style.bodyAlign, numeric ? 'right' : 'left'),
                                                    fontVariantNumeric: numeric ? 'tabular-nums' : 'normal',
                                                    fontWeight: cf.fontWeight || (idx === 0 || merged ? 600 : (style.bodyFontWeight || 400)),
                                                    color: cf.color || (idx === 0 || merged ? dimFg : bodyFg),
                                                    verticalAlign: merged ? 'middle' : 'top',
                                                    background: cf.background || (merged ? stripeBg : stripe),
                                                    boxShadow: merged ? `inset 3px 0 0 ${exec ? EXEC.accentSoft : headerBg}` : undefined,
                                                    ...(exec ? { borderBottom: `1px solid ${EXEC.lineSoft}` } : {}),
                                                    ...stickyFirst(idx)
                                                }}
                                            >
                                                {cellText(header, row[header], numeric)}
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                        {style.showTotals && Object.keys(totals).length > 0 && (
                            <tfoot>
                                <tr>
                                    {headers.map((header, idx) => {
                                        const cf = firstConditionalStyle(query.conditionalFormats?.[header], totals[header]);
                                        return (
                                        <td
                                            key={header}
                                            style={{
                                                ...styles.td,
                                                ...colWidth(header),
                                                padding: cellPad,
                                                fontSize: totalsFontSize,
                                                fontFamily: tableFont,
                                                fontStyle: style.totalsItalic ? 'italic' : 'normal',
                                                fontWeight: cf.fontWeight || style.totalsFontWeight || 700,
                                                background: cf.background || totalsBg,
                                                color: cf.color || totalsFg,
                                                textAlign: alignOf(style.totalsAlign, totals[header] != null ? 'right' : 'left'),
                                                fontVariantNumeric: 'tabular-nums',
                                                ...(exec ? { borderTop: execRule, borderBottom: 'none' } : {}),
                                                ...stickyFirst(idx)
                                            }}
                                        >
                                            {idx === 0 && totals[header] == null ? 'Total' : (totals[header] != null ? fmt(totals[header], header) : '')}
                                        </td>
                                        );
                                    })}
                                </tr>
                            </tfoot>
                        )}
                    </table>
                </div>
                {paginate && (
                <div style={styles.pager}>
                    <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(0); }} style={styles.pageSize}>
                        {[10, 25, 50, 100].map(size => <option key={size} value={size}>{size} / pág.</option>)}
                    </select>
                    <span>
                        {sortedRows.length === 0 ? '0' : `${safePage * pageSize + 1}–${Math.min(sortedRows.length, (safePage + 1) * pageSize)}`} de {sortedRows.length}
                    </span>
                    <button type="button" style={styles.pagerBtn} disabled={safePage === 0} onClick={() => setPage(p => p - 1)}>‹</button>
                    <button type="button" style={styles.pagerBtn} disabled={safePage >= pageCount - 1} onClick={() => setPage(p => p + 1)}>›</button>
                </div>
                )}
            </div>
        );
    };

    const renderText = () => (
        <div style={getTextContentStyle(style)}>
            {config.content || ''}
        </div>
    );

    const renderImage = () => {
        const src = String(config.content || style.imageUrl || '').trim();
        if (!src) {
            return <div style={styles.emptyState}>Añade una imagen en Propiedades (subir archivo o URL).</div>;
        }
        return (
            <img
                src={src}
                alt={config.title || ''}
                style={{
                    width: '100%',
                    height: '100%',
                    objectFit: style.objectFit || 'contain',
                    objectPosition: nested ? 'top' : 'center',
                    display: 'block',
                    pointerEvents: 'none',
                }}
            />
        );
    };

    const renderDivider = () => {
        const vertical = style.orientation === 'vertical';
        const thickness = style.thickness || 2;
        return (
            <div style={{
                ...styles.dividerWrap,
                ...(nested ? styles.dividerWrapTop : {})
            }}>
                <div style={{
                    background: style.color || '#5E35B1',
                    width: vertical ? thickness : '100%',
                    height: vertical ? '100%' : thickness,
                    borderRadius: 99
                }} />
            </div>
        );
    };

    const renderContainer = () => {
        const innerWidgets = config.children?.widgets || [];
        const innerLayout = config.children?.layout || [];
        if (innerWidgets.length === 0) {
            return <div style={styles.emptyState}>Este bloque está vacío.</div>;
        }
        const innerCols = INNER_CANVAS_COLUMNS;
        const gap = Math.max(0, Number(style.innerGap ?? 0));
        const pad = Math.max(0, Number(style.innerPadding ?? 0));
        const innerRowHeight = Math.max(12, Number(canvasRowHeight) || DEFAULT_CANVAS.rowHeight || 20);
        if (stacked) {
            /* Móvil: los hijos del bloque se apilan en una columna a todo el ancho. */
            return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: Math.max(gap, 8), padding: pad }}>
                    {stackWidgets(innerWidgets, innerLayout).map(({ widget: child, item }) => (
                        <div
                            key={child.id}
                            style={{
                                ...widgetCardChrome({
                                    backgroundColor: child.style?.cardBackground || '#fff',
                                    borderRadius: child.style?.cardRadius ?? 8,
                                }, theme),
                                overflow: child.type === 'text' ? 'auto' : 'hidden',
                                height: stackedHeight(child, item, innerRowHeight, gap),
                                width: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            <WidgetRenderer
                                nested
                                stacked
                                canvasRowHeight={canvasRowHeight}
                                config={child}
                                engineFilters={engineFilters}
                                showTitle={child.style?.showTitle !== false}
                                filterValues={filterValues}
                                schemaColumns={schemasBySource[child.query?.dataSourceId]?.columns || schemaColumns}
                                schemasBySource={schemasBySource}
                                filterDefs={filterDefs}
                                dataSources={dataSources}
                                joins={joins}
                                onFilterValueChange={onFilterValueChange}
                                exportable={exportable}
                            />
                        </div>
                    ))}
                </div>
            );
        }
        return (
            <div style={{ ...styles.innerCanvas, padding: pad }}>
                <MeasuredGrid
                    className="inner-layout"
                    layout={innerLayout}
                    cols={innerCols}
                    rowHeight={Math.max(12, Number(canvasRowHeight) || DEFAULT_CANVAS.rowHeight || 20)}
                    margin={[gap, gap]}
                    compactType={null}
                    isDraggable={false}
                    isResizable={false}
                    fillContainer
                >
                    {innerWidgets.map(child => {
                        const pinTop = hugsBlockTop(child.type);
                        return (
                        <div
                            key={child.id}
                            style={{
                                ...widgetCardChrome({
                                    backgroundColor: child.style?.cardBackground || '#fff',
                                    borderRadius: child.style?.cardRadius ?? 8,
                                }, theme),
                                overflow: child.type === 'text' ? 'auto' : 'hidden',
                                height: pinTop ? 'auto' : '100%',
                                maxHeight: '100%',
                                width: '100%',
                                alignSelf: pinTop ? 'flex-start' : 'stretch',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'flex-start',
                                zIndex: (Number(child.z) || 0) + 1
                            }}
                        >
                            <WidgetRenderer
                                nested
                                canvasRowHeight={canvasRowHeight}
                                config={child}
                                engineFilters={engineFilters}
                                showTitle={child.style?.showTitle !== false}
                                filterValues={filterValues}
                                schemaColumns={schemasBySource[child.query?.dataSourceId]?.columns || schemaColumns}
                                schemasBySource={schemasBySource}
                                filterDefs={filterDefs}
                                dataSources={dataSources}
                                joins={joins}
                                onFilterValueChange={onFilterValueChange}
                                exportable={exportable}
                            />
                        </div>
                        );
                    })}
                </MeasuredGrid>
            </div>
        );
    };

    const body = () => {
        if (widgetType === 'text') return renderText();
        if (widgetType === 'image') return renderImage();
        if (widgetType === 'divider') return renderDivider();
        if (widgetType === 'container') return renderContainer();
        if (widgetType === 'slicer') return renderSlicer();
        if (widgetType === 'kpi') return renderKpi();
        if (widgetType === 'map_mx') return renderMap();
        if (isChartType(widgetType)) return renderChart();
        return renderTable();
    };

    const skipEngine = isDecorationType(widgetType) || isContainerType(widgetType);
    const engineBacked = !skipEngine && widgetType !== 'slicer';
    const firstLoad = loading && engineBacked && data.length === 0;
    const refreshing = loading && engineBacked && data.length > 0;
    const isBlock = isContainerType(widgetType);
    const frame = isBlock ? getBlockFrameStyle(style) : {};

    const fileBase = sanitizeFilename(title || widgetType || 'elemento');

    const chromePad = (widgetType === 'text' || widgetType === 'image')
        ? { paddingTop: 0, paddingRight: 0, paddingBottom: 0, paddingLeft: 0 }
        : isBlock
            ? {
                paddingTop: Number(frame.padding) || 0,
                paddingRight: Number(frame.padding) || 0,
                paddingBottom: Number(frame.padding) || 0,
                paddingLeft: Number(frame.padding) || 0,
            }
            : { paddingTop: 10, paddingRight: 12, paddingBottom: 10, paddingLeft: 12 };
    if (canExport && widgetType !== 'text' && widgetType !== 'image') {
        chromePad.paddingRight = Math.max(chromePad.paddingRight, 40);
    }

    return (
        <div
            ref={captureRef}
            data-export-block="true"
            style={{
                ...styles.container,
                ...(nested ? { justifyContent: 'flex-start' } : {}),
                ...(nested && hugsBlockTop(widgetType) ? styles.containerTop : {}),
                position: 'relative',
                ...chromePad,
                boxSizing: 'border-box'
            }}
        >
            {canExport && (
                <WidgetExportMenu
                    onPng={() => exportElementToPng(captureRef.current, fileBase)}
                    onPdf={() => exportElementToPdf(captureRef.current, fileBase, { title: title || fileBase })}
                    onExcel={() => exportWidgetToExcel(config, {
                        filename: fileBase,
                        records: sortedRows,
                        engineFilters: scopedFilters,
                        filterValues,
                        dataSources,
                        joins
                    })}
                />
            )}
            {showTitle && style.showTitle !== false && title && widgetType !== 'slicer' && !isDecorationType(widgetType) ? (
                <h3 style={isBlock
                    ? { ...getBlockTitleStyle(style), marginBottom: style.subtitle ? 2 : 4 }
                    : (exec ? execStyles.title : styles.title)}>{title}</h3>
            ) : null}
            {isBlock && style.subtitle ? (
                <p style={{ ...getBlockSubtitleStyle(style), marginBottom: 8 }}>{style.subtitle}</p>
            ) : null}
            {/* Primera carga: skeleton. Al refrescar se conservan los datos, atenuados, con una barra arriba. */}
            {refreshing && <div className="cm-refresh-bar" style={styles.refreshBar} aria-hidden="true" />}
            {firstLoad && (
                <div style={styles.bodySlot} aria-busy="true" aria-label="Cargando datos">
                    <WidgetSkeleton type={widgetType} />
                </div>
            )}
            {error && !skipEngine && (
                <div style={styles.error}>
                    <span>{error}</span>
                    <button type="button" style={styles.retryBtn} onClick={() => setRetryNonce(n => n + 1)}>
                        Reintentar
                    </button>
                </div>
            )}
            {notice && !error && !skipEngine && <div style={styles.notice}>{notice}</div>}
            {!firstLoad && !error && (
                <div style={{ ...styles.bodySlot, opacity: refreshing ? 0.55 : 1, transition: 'opacity 0.2s ease' }}>{body()}</div>
            )}
        </div>
    );
};

const styles = {
    container: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        padding: '10px 12px',
        minHeight: 0,
        boxSizing: 'border-box'
    },
    containerTop: {
        justifyContent: 'flex-start',
        height: 'auto',
        maxHeight: '100%'
    },
    title: { margin: '0 0 8px 0', fontSize: '0.95rem', color: '#4a148c', flexShrink: 0 },
    bodySlot: {
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
    },
    status: { color: '#5f6368', fontSize: '0.85rem', padding: '12px 0' },
    error: {
        backgroundColor: '#fce8e6',
        color: '#c5221f',
        padding: '8px 10px',
        borderRadius: '8px',
        fontSize: '0.85rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
        flexWrap: 'wrap',
    },
    retryBtn: {
        border: '1px solid #c5221f',
        backgroundColor: '#ffffff',
        color: '#c5221f',
        borderRadius: 6,
        padding: '3px 10px',
        fontSize: '0.78rem',
        fontWeight: 600,
        cursor: 'pointer',
        fontFamily: 'inherit',
    },
    refreshBar: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: 2,
        borderRadius: 2,
        overflow: 'hidden',
        zIndex: 6,
    },
    notice: {
        backgroundColor: '#fff8e1',
        color: '#b06000',
        padding: '8px 10px',
        borderRadius: '8px',
        fontSize: '0.8rem',
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: '#f9ab00',
        marginBottom: 8,
    },
    emptyState: {
        color: '#3c4043',
        fontSize: '0.85rem',
        padding: '16px 12px',
        textAlign: 'center',
        backgroundColor: '#fafafa',
        borderWidth: 1,
        borderStyle: 'dashed',
        borderColor: '#c4c7cc',
        borderRadius: 8,
    },
    mapTooltip: {
        position: 'absolute',
        pointerEvents: 'none',
        backgroundColor: '#202124',
        color: '#ffffff',
        fontSize: '0.78rem',
        lineHeight: 1.35,
        padding: '6px 10px',
        borderRadius: 6,
        boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
        zIndex: 5,
        whiteSpace: 'nowrap'
    },
    mapLegend: { display: 'flex', flexDirection: 'column', gap: 2, flexShrink: 0, padding: '0 4px' },
    mapLegendBar: { height: 8, borderRadius: 4 },
    mapLegendLabels: {
        display: 'flex', justifyContent: 'space-between', fontSize: '0.72rem', color: '#5f6368'
    },
    mapLegendNoData: {
        display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.72rem', color: '#5f6368', marginTop: 2
    },
    mapLegendSwatch: {
        width: 10, height: 10, borderRadius: 3, borderWidth: 1, borderStyle: 'solid', borderColor: '#c4c7cc'
    },
    mapZoomControls: {
        position: 'absolute',
        right: 8,
        bottom: 8,
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        zIndex: 4
    },
    mapZoomBtn: {
        width: 24,
        height: 24,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: '#c4c7cc',
        borderRadius: 6,
        backgroundColor: 'rgba(255,255,255,0.92)',
        color: '#202124',
        fontSize: '0.9rem',
        lineHeight: 1,
        cursor: 'pointer',
        boxShadow: '0 1px 3px rgba(28,29,33,0.15)',
        fontFamily: 'inherit'
    },
    tableShell: { display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, height: '100%' },
    tableWrap: {
        overflow: 'auto',
        flex: 1,
        minHeight: 0,
        border: '1px solid #e8def8',
        borderRadius: '10px'
    },
    tableWrapFit: {
        overflowX: 'hidden',
        overflowY: 'auto',
        flex: 1,
        minHeight: 0,
        border: '1px solid #e8def8',
        borderRadius: '10px'
    },
    table: { width: '100%', borderCollapse: 'separate', borderSpacing: 0 },
    th: {
        textAlign: 'left',
        fontWeight: 600,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        position: 'sticky',
        top: 0,
        letterSpacing: '0.02em',
        overflow: 'hidden'
    },
    resizeHandle: {
        position: 'absolute',
        top: 0,
        right: 0,
        width: '8px',
        height: '100%',
        cursor: 'col-resize',
        zIndex: 4
    },
    td: { whiteSpace: 'nowrap', borderBottom: '1px solid #f0eaf8', overflow: 'hidden', textOverflow: 'ellipsis' },
    pager: {
        display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '8px',
        padding: '2px 0 0', fontSize: '0.8rem', color: '#5f6368', flexShrink: 0
    },
    pageSize: { border: '1px solid #dadce0', borderRadius: '6px', padding: '3px 6px', fontSize: '0.78rem' },
    pagerBtn: { border: '1px solid #dadce0', background: '#fff', borderRadius: '6px', cursor: 'pointer', padding: '3px 10px' },
    slicerBox: {
        display: 'flex',
        alignItems: 'center',
        height: '100%',
        padding: '10px 12px',
        boxSizing: 'border-box',
        backgroundColor: '#f4f5f7',
        borderRadius: 12
    },
    slicerBoxTop: {
        alignItems: 'flex-start',
        height: 'auto'
    },
    textBox: { flex: 1, padding: '8px 4px', whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.45, overflow: 'auto', minHeight: 0 },
    dividerWrap: { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', padding: '8px' },
    dividerWrapTop: { alignItems: 'flex-start', justifyContent: 'flex-start', height: 'auto', padding: '8px 8px 0' },
    kpiRow: { display: 'flex', flex: 1, alignItems: 'center', justifyContent: 'space-around', gap: '8px', flexWrap: 'wrap' },
    kpiRowTop: { alignItems: 'flex-start', justifyContent: 'flex-start', flex: '0 1 auto' },
    kpiCard: { textAlign: 'center', minWidth: '90px' },
    kpiValue: { fontWeight: 700, lineHeight: 1.1 },
    kpiLabel: { fontSize: '0.8rem', color: '#5f6368', marginTop: '6px' },
    innerCanvas: { flex: 1, minHeight: 0, height: '100%', width: '100%', overflow: 'hidden' }
};

/** KPI: delta contra el periodo anterior, avance contra la meta y sparkline. */
const kpiStyles = {
    cardWide: { width: '100%', maxWidth: 260 },
    period: { color: '#80868b', fontWeight: 400 },
    delta: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        marginTop: 6,
        padding: '2px 8px',
        borderRadius: 999,
        fontSize: '0.75rem',
        fontWeight: 600,
        fontVariantNumeric: 'tabular-nums',
        whiteSpace: 'nowrap',
    },
    deltaGood: { color: '#137333', backgroundColor: '#e6f4ea' },
    deltaBad: { color: '#c5221f', backgroundColor: '#fce8e6' },
    deltaNeutral: { color: '#5f6368', backgroundColor: '#f1f3f4', fontWeight: 500 },
    versus: { fontWeight: 400, opacity: 0.85 },
    target: { display: 'flex', flexDirection: 'column', gap: 4, marginTop: 8, width: '100%' },
    targetTrack: { height: 6, borderRadius: 999, backgroundColor: '#eceef2', overflow: 'hidden' },
    targetFill: { height: '100%', borderRadius: 999 },
    targetText: { fontSize: '0.72rem', color: '#5f6368', fontVariantNumeric: 'tabular-nums' },
    spark: { width: '100%', height: 36, marginTop: 6 },
};

/** Piezas propias del tema ejecutivo y del skeleton de carga. */
const execStyles = {
    title: { margin: '0 0 10px 0', fontSize: '0.9rem', fontWeight: 600, color: EXEC.ink, letterSpacing: '-0.01em', flexShrink: 0 },
    tooltip: {
        backgroundColor: EXEC.surface,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: EXEC.line,
        borderRadius: 10,
        boxShadow: EXEC.tooltipShadow,
        padding: '8px 10px',
        minWidth: 140,
        fontSize: EXEC.fontSize,
        color: EXEC.ink,
    },
    tooltipTitle: { fontWeight: 600, marginBottom: 6, color: EXEC.ink },
    tooltipRow: { display: 'flex', alignItems: 'center', gap: 8, lineHeight: 1.7 },
    tooltipDot: { width: 8, height: 8, borderRadius: '50%', flexShrink: 0 },
    tooltipName: { color: EXEC.muted, flex: 1, whiteSpace: 'nowrap' },
    tooltipValue: { fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: EXEC.ink, whiteSpace: 'nowrap' },
    kpiLabel: { fontSize: '0.78rem', color: EXEC.muted, fontWeight: 500, marginBottom: 4 },
    kpiValue: { fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em' },
    skelKpi: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, flex: 1 },
    skelTable: { display: 'flex', flexDirection: 'column', gap: 10, padding: '4px 0' },
    skelCenter: { display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 },
    skelBars: { display: 'flex', alignItems: 'flex-end', gap: 10, flex: 1, minHeight: 60, padding: '8px 4px 0' },
    pieCenter: {
        position: 'absolute',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'none',
    },
    pieTotal: { fontSize: 18, fontWeight: 600, color: EXEC.ink, fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 },
    pieTotalLabel: { fontSize: 11, color: EXEC.muted, marginTop: 2 },
    pieLegend: {
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        gap: '4px 12px',
        fontSize: EXEC.fontSize,
        color: EXEC.muted,
        padding: '4px 4px 0',
        flexShrink: 0,
    },
    pieLegendItem: { display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' },
};

const ResizableTh = ({ header, width, onResize, onResizeStart, onClick, style, children }) => {
    const startRef = useRef(null);

    const onMouseDown = (event) => {
        event.preventDefault();
        event.stopPropagation();
        const startX = event.clientX;
        const startW = width || event.currentTarget.parentElement.getBoundingClientRect().width;
        const locked = onResizeStart ? onResizeStart() : {};
        startRef.current = { startX, startW, locked };
        const apply = (clientX, persist) => {
            onResize(header, Math.max(48, startRef.current.startW + clientX - startRef.current.startX), persist, startRef.current.locked);
        };
        const move = (ev) => apply(ev.clientX, false);
        const up = (ev) => {
            document.removeEventListener('mousemove', move);
            document.removeEventListener('mouseup', up);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
            apply(ev.clientX, true);
        };
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
        document.addEventListener('mousemove', move);
        document.addEventListener('mouseup', up);
    };

    return (
        <th onClick={onClick} style={style}>
            {children}
            <span onMouseDown={onMouseDown} style={styles.resizeHandle} title="Arrastra para ajustar el ancho" />
        </th>
    );
};

export default WidgetRenderer;
